import React, { createContext, useState, useContext } from "react";
import { ethers } from "ethers";
import { NETWORK } from "../config/config";

const Web3Context = createContext(null);

export function Web3Provider({ children }) {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const isCorrectChain = chainId === NETWORK.chainId;

  async function switchNetwork() {
    if (!window.ethereum) return;
    const hexChainId = NETWORK.chainIdHex;
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hexChainId }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: hexChainId,
              chainName: NETWORK.chainName,
              nativeCurrency: NETWORK.nativeCurrency,
              rpcUrls: [NETWORK.rpcUrl],
              blockExplorerUrls: [NETWORK.blockExplorer],
            },
          ],
        });
      } else {
        throw switchError;
      }
    }
  }

  async function connectWallet() {
    if (!window.ethereum) {
      throw new Error("No wallet detected. Please install MetaMask or use Trust Wallet.");
    }
    setConnecting(true);
    try {
      const accounts = await window.ethereum.request({
        method: "eth_requestAccounts",
      });
      const addr = accounts[0];

      const currentChainId = await window.ethereum.request({ method: "eth_chainId" });
      const numericChainId = parseInt(currentChainId, 16);

      if (numericChainId !== NETWORK.chainId) {
        await switchNetwork();
      }

      const web3Provider = new ethers.providers.Web3Provider(window.ethereum, "any");
      const web3Signer = web3Provider.getSigner();

      const network = await web3Provider.getNetwork();
      setChainId(network.chainId);
      setAccount(addr);
      setProvider(web3Provider);
      setSigner(web3Signer);
    } finally {
      setConnecting(false);
    }
  }

  async function refreshAccount() {
    if (!window.ethereum) return;
    try {
      const accounts = await window.ethereum.request({ method: "eth_accounts" });
      if (accounts.length === 0) {
        setAccount(null);
        setProvider(null);
        setSigner(null);
        setChainId(null);
        return;
      }
      const addr = accounts[0];
      const currentChainId = await window.ethereum.request({ method: "eth_chainId" });
      const numericChainId = parseInt(currentChainId, 16);

      if (numericChainId !== NETWORK.chainId) {
        await switchNetwork();
      }

      const web3Provider = new ethers.providers.Web3Provider(window.ethereum, "any");
      const web3Signer = web3Provider.getSigner();
      const network = await web3Provider.getNetwork();

      if (addr !== account) setAccount(addr);
      setChainId(network.chainId);
      setProvider(web3Provider);
      setSigner(web3Signer);
    } catch (err) {
      console.error("refreshAccount error:", err);
    }
  }

  function disconnect() {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setChainId(null);
  }

  return (
    <Web3Context.Provider
      value={{
        account,
        provider,
        signer,
        chainId,
        isCorrectChain,
        connecting,
        connectWallet,
        refreshAccount,
        disconnect,
      }}
    >
      {children}
    </Web3Context.Provider>
  );
}

export function useWeb3() {
  const ctx = useContext(Web3Context);
  if (!ctx) throw new Error("useWeb3 must be used within Web3Provider");
  return ctx;
}
